const text = (value) => typeof value === "string" ? value.trim() : "";

export function normalizeApplicationDiagnostic(item) {
  if (!item || typeof item !== "object") return null;
  const orderId = item.order_id || item.orderId;
  const orderIds = item.order_ids || item.orderIds || (orderId ? [orderId] : []);
  return {
    ...item,
    kind: text(item.kind), reasonCode: text(item.reason_code || item.reasonCode), message: text(item.message),
    label: text(item.label), orderIds: Array.isArray(orderIds) ? orderIds.map(text).filter(Boolean) : [],
    sourceGroupKey: text(item.source_group_key || item.sourceGroupKey),
    deliveryId: text(item.delivery_id || item.deliveryId),
    constraintType: text(item.constraint_type || item.constraintType),
    plannedTime: item.planned_time ?? item.plannedTime ?? null,
    newLimit: item.new_limit ?? item.newLimit ?? null,
    plannedValue: item.planned_value ?? item.plannedValue ?? null,
    limit: item.limit ?? null, unit: text(item.unit),
    resolutionMessage: text(item.resolution_message || item.resolutionMessage),
    resolutionReasonCode: text(item.resolution_reason_code || item.resolutionReasonCode),
  };
}

export function normalizeApplicationFeedback(value) {
  if (!value || typeof value !== "object" || !Array.isArray(value.diagnostics)) return null;
  return {
    outcome: text(value.outcome), kind: text(value.kind), origin: text(value.origin),
    recordedAt: text(value.recorded_at || value.recordedAt),
    diagnostics: value.diagnostics.map(normalizeApplicationDiagnostic).filter((item) => item?.message),
  };
}

const reasonAliases = {
  time_insufficient: "max_work_hours_exceeded", strict_time_limit_exceeded: "max_work_hours_exceeded",
  capacity_insufficient: "load_capacity_exceeded", compartment_capacity_insufficient: "load_capacity_exceeded",
};
const CARD_LEVEL_REASON_SUMMARIES = new Set([
  "local_feasibility_failed",
  "manual_sequence_not_replayable",
  "structural_truck_change",
]);
const reason = (code) => reasonAliases[code] || code;
const constraint = (type) => ({ client_closing: "client_loading_closing_time", client_opening: "client_loading_opening_time" }[type] || type);
const finite = (value) => typeof value === "number" && Number.isFinite(value);
const numberLabel = (value) => new Intl.NumberFormat("fr-CA", { maximumFractionDigits: 2 }).format(value);
export function feedbackDurationLabel(value) {
  const minutes = Math.round(value);
  const hours = Math.floor(minutes / 60);
  return hours ? `${hours} h${minutes % 60 ? ` ${String(minutes % 60).padStart(2, "0")}` : ""}` : `${minutes} min`;
}

function diagnosticMessage(item) {
  let message = item.message;
  if (["Le calcul du camion n’a pas pu être validé. Aucun conflit métier précis n’a été confirmé.",
    "Le calcul n’a pas pu être validé. Aucun conflit métier précis n’a pu être confirmé."].includes(message)) {
    message = "Le recalcul a échoué ; la cause n’a pas pu être déterminée.";
  }
  if (finite(item.plannedValue) && finite(item.limit)) {
    if (item.unit === "tons") {
      message = `Chargement${item.label ? ` chez ${item.label}` : ""} : ${numberLabel(item.plannedValue)} t pour ${numberLabel(item.limit)} t disponibles${item.plannedValue > item.limit ? `, soit ${numberLabel(item.plannedValue - item.limit)} t de trop` : ""}.`;
    } else if (item.unit === "minutes") {
      const flex = item.flex_minutes ?? item.flexMinutes;
      const budgetLabel = item.limit_basis === "remaining_budget" ? "restantes" : "disponibles";
      message = `${item.duration_basis === "retained_route" || item.durationBasis === "retained_route" ? "Tournée actuelle" : "Durée calculée"} : ${feedbackDurationLabel(item.plannedValue)} pour ${feedbackDurationLabel(item.limit)} ${budgetLabel}${finite(flex) && flex > 0 ? `, incluant ${feedbackDurationLabel(flex)} de souplesse` : ""}.`;
      if (item.strict_time_limits && item.overtime_authorized) {
        message += " La limite stricte du recalcul s’applique malgré les heures supplémentaires autorisées.";
      }
    }
  }
  if (item.label && message && !message.toLocaleLowerCase("fr").includes(item.label.toLocaleLowerCase("fr"))) {
    message = `${item.label} : ${message}`;
  }
  return message;
}

function sameProblem(left, right) {
  if (reason(left.reasonCode) !== reason(right.reasonCode) || constraint(left.constraintType) !== constraint(right.constraintType)) return false;
  if (left.sourceGroupKey && right.sourceGroupKey && left.sourceGroupKey !== right.sourceGroupKey) return false;
  if (left.label && right.label && left.label !== right.label) return false;
  if (left.deliveryId && right.deliveryId && left.deliveryId !== right.deliveryId) return false;
  if (left.orderIds.length || right.orderIds.length) {
    if (!left.orderIds.length || !right.orderIds.length) return false;
    const [smaller, larger] = left.orderIds.length <= right.orderIds.length ? [left.orderIds, right.orderIds] : [right.orderIds, left.orderIds];
    if (!smaller.every((id) => larger.includes(id))) return false;
  } else if (left.label !== right.label) return false;
  const values = ["plannedValue", "limit", "unit", "plannedTime", "newLimit"];
  if (values.some((key) => left[key] !== right[key])) return false;
  return values.some((key) => key !== "unit" && left[key] != null) || left.message === right.message;
}

/** Merge display evidence without changing the stored diagnostics or action state. */
export function buildApplicationFeedbackItems(feedback, { diagnostics = [], fallbackDiagnostics = [] } = {}) {
  const items = [];
  const primary = normalizeApplicationFeedback(feedback)?.diagnostics || [];
  for (const raw of [...primary, ...diagnostics]) {
    const item = normalizeApplicationDiagnostic(raw);
    if (!item) continue;
    item.message = diagnosticMessage(item);
    if (!item.message) continue;
    const existing = items.find((other) => sameProblem(other, item) || (item.summary === true
      && reason(other.reasonCode) === reason(item.reasonCode) && other.unit === item.unit
      && finite(item.plannedValue) && finite(item.limit)
      && other.plannedValue === item.plannedValue && other.limit === item.limit));
    if (existing) {
      existing.orderIds = [...new Set([...existing.orderIds, ...item.orderIds])];
      if (item.resolutionMessage && item.resolutionMessage !== existing.resolutionMessage) {
        if (!existing.resolutionMessage) {
          existing.resolutionMessage = item.resolutionMessage;
          existing.resolutionReasonCode = item.resolutionReasonCode;
        } else if (!items.some((other) => other.instructionOnly && other.message === item.resolutionMessage && sameProblem(other, item))) {
          items.push({ ...item, message: item.resolutionMessage, resolutionMessage: "", instructionOnly: true });
        }
      }
    } else items.push(item);
  }
  for (const raw of fallbackDiagnostics) {
    const item = normalizeApplicationDiagnostic(raw);
    if (!item?.message) continue;
    // These are card-level reason summaries; precise evidence takes precedence.
    if (item.reasonCode && items.some((other) => reason(other.reasonCode) === reason(item.reasonCode)
      || other.resolutionReasonCode === item.reasonCode
      || other.supersedes_reason_codes?.includes(item.reasonCode))) continue;
    if (CARD_LEVEL_REASON_SUMMARIES.has(item.reasonCode) && items.length) continue;
    if (!items.some((other) => sameProblem(other, item))) items.push(item);
  }
  const precise = items.filter((item) => !CARD_LEVEL_REASON_SUMMARIES.has(item.reasonCode));
  return precise.length ? precise : items;
}

export function applicationFeedbackMessage(value) {
  const feedback = normalizeApplicationFeedback(value);
  return feedback?.diagnostics.map((item) => [item.message, item.resolutionMessage].filter(Boolean).join(" ")).join(" ") || "";
}

export function isBusinessApplicationBlock(value) {
  return value?.application_feedback?.kind === "business" || value?.applicationFeedback?.kind === "business";
}
